// EntityForm.tsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Typography,
  Stack,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Button,
} from '@mui/material';
import { Formik, Form, Field, type FormikProps, type FieldProps } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { Entity } from '../../../types/entity';
import { useAuthStore } from '../../../store/useAuthStore3';

export interface EntityFormProps {
  entity: Entity | null;
  type: number;
  onSave: (values: Entity) => void;
  onCancel: () => void;
  parents: Entity[];
}

const EntityForm: React.FC<EntityFormProps> = ({
  entity,
  type,
  onSave,
  onCancel,
  parents,
}) => {
  const { t } = useTranslation();
  const title = type == 1 ?
    (entity ? t('EntityForm.Edit_location') : t('EntityForm.New_location')) :
    (entity ? t('EntityForm.Edit_checkpoint') : t('EntityForm.New_checkpoint'));

  const { customerId } = useAuthStore();

  const initialValues: Entity = entity || {
    customer_id: customerId || -1,
    entity_id: -1,
    parent_id: parents.length == 1 ? parents[0].entity_id : -1,
    name: '',
    type: type,
  };

  const validationSchema = Yup.object({
    entity_id: Yup.number().required(('entity_is_required')),
    parent_id: Yup.number()
      .required(('parent_is_required'))
      .test(
        'not-minus-one', 
        'parent_is_required', // Ваше сообщение об ошибке, если значение равно -1
        (value) => value !== -1
      ),
  });

  if (customerId === -1) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 3 }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>{('Loading_form_data')}</Typography>
      </Box>
    );
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={(values) => {
        onSave(values);
      }}
      enableReinitialize
    >
      {(formikProps: FormikProps<Entity>) => {
        const { values, setFieldValue } = formikProps;

        return (
          <Form>
            <Typography variant="h6" gutterBottom>
              {title}
            </Typography>
        
            <Stack spacing={2} sx={{ mt: 2 }}>
              <Field name="name">
                {({ field, meta }: { field: any; meta: any }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label={t('EntityForm.name_Label')}
                    error={meta.touched && !!meta.error}
                    helperText={meta.touched && meta.error}
                  />
                )}
              </Field>

              {type !== 1 && (
              <Field name="parent_id">
                {({ field, meta }: { field: any; meta: any }) => (
                  <FormControl fullWidth error={meta.touched && !!meta.error}>
                    <InputLabel id="point-id-label">{t('EntityForm.parent_Label')}</InputLabel>
                    <Select
                      labelId="parent-id-label"
                      label={t('EntityForm.parent_Label')}
                      {...field}
                    >
                      <MenuItem value=""><em>{t('EntityForm.Select_a_parent')}</em></MenuItem>
                      {parents.map((parent) => (
                        <MenuItem key={parent.entity_id} value={parent.entity_id}>
                          {parent.name}
                        </MenuItem>
                      ))}
                    </Select>
                    {meta.touched && meta.error && (
                      <Typography color="error" variant="caption" component="div">{meta.error}</Typography>
                    )}
                  </FormControl>
                )}
              </Field>
              )}

            </Stack>

            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button onClick={onCancel} sx={{ mr: 1 }} disabled={formikProps.isSubmitting} variant='outlined'>
                  {t('EntityForm.Cancel')}
                </Button>
                <Button type="submit" variant="contained" disabled={formikProps.isSubmitting || !formikProps.isValid || !formikProps.dirty}>
                  {formikProps.isSubmitting ? t('EntityForm.Saving') : t('EntityForm.Save')}
                </Button>
              </Box>
            </Box>
          </Form>
        );
      }}
    </Formik>
  );
};

export default EntityForm;